import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LoginRequest, LoginResponse } from '../models/auth.model';
import { UserLocalStorageObject } from './local-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly tokenKey = 'auth_token';
  private readonly usernameKey = 'auth_username';

  userData = new BehaviorSubject<UserLocalStorageObject | null>(null);

  constructor(private http: HttpClient) {}

  login(username: string, password: string): Observable<LoginResponse> {
    const payload: LoginRequest = { username, password };
    return this.http
      .post<LoginResponse>(`${environment.serverUrl}account/login`, payload)
      .pipe(
        tap((res) => {
          localStorage.setItem(this.tokenKey, res.token);
          localStorage.setItem(this.usernameKey, res.username ?? username);
          this.userData.next(new UserLocalStorageObject(res.username ?? username, res.token));
          localStorage.setItem(environment.userSessionUniqueId, JSON.stringify(this.userData.value));
        }),
        catchError((err: HttpErrorResponse) => throwError(() => err))
      );
  }

  autoLogin() {
    const storedUserData = localStorage.getItem(environment.userSessionUniqueId);
    if (!storedUserData) {
      return;
    }
    const userData: UserLocalStorageObject = JSON.parse(storedUserData);
    const loadedUser = new UserLocalStorageObject(
      userData.username,
      userData.sessionToken
    );
    if (loadedUser.sessionToken) {
      this.userData.next(loadedUser);
    }
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.usernameKey);
    this.userData.next(null);
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem(this.tokenKey);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  getUsername(): string | null {
    return localStorage.getItem(this.usernameKey);
  }

  public get currentUserDataValue(): UserLocalStorageObject | null {
    return this.userData.value;
  }
}
